import {useEffect, useState} from 'react'

import {keyBy, omit} from 'es-toolkit'

import {getUniqueFilename} from '../libs/file'
import {uuid} from '../services/uuid'

import {AttachmentFile, FileId} from '../types'

type UseAttachmentOptions = {
  fileSizeLimit: number
  enableSrc?: boolean
  uniqueFileNames?: boolean
}

const renameFile = (file: File, fileName: string): File => {
  return new File([file], fileName, {
    type: file.type,
    lastModified: file.lastModified,
  })
}

const getUniqueFile = (attachments: Record<FileId, AttachmentFile>, file: File): File => {
  const existingFileNames = Object.values(attachments).map(attachment => attachment.name)
  return renameFile(file, getUniqueFilename(file.name, existingFileNames))
}

const useAttachments = (options: UseAttachmentOptions) => {
  const {fileSizeLimit, enableSrc = false, uniqueFileNames = false} = options
  const [attachments, setAttachments] = useState<Record<FileId, AttachmentFile>>({})
  const [fileSizeExceededCount, setFileSizeExceededCount] = useState(0)

  const addAttachments = (files: File[], type?: string): FileId[] => {
    const fileNamesExceedingSizeLimit: string[] = []

    const nextFiles = files.filter(file => {
      if (file.size > fileSizeLimit) {
        fileNamesExceedingSizeLimit.push(file.name)
        return false
      }
      return true
    })

    if (!nextFiles.length && !fileNamesExceedingSizeLimit.length) {
      return []
    }

    const uploadingFiles = Array.from(nextFiles).map(file => {
      const uniqueFile = uniqueFileNames ? getUniqueFile(attachments, file) : file
      return {
        file: uniqueFile,
        id: uuid(),
        name: uniqueFile.name,
        size: uniqueFile.size,
        type,
        src: enableSrc ? URL.createObjectURL(uniqueFile) : undefined,
      }
    })

    setAttachments({
      ...attachments,
      ...keyBy(uploadingFiles, file => file.id),
    })

    setFileSizeExceededCount(fileNamesExceedingSizeLimit.length || 0)

    return uploadingFiles.map(({id}) => id)
  }

  const deleteAttachment = (fileId: FileId) => {
    const file = attachments[fileId]

    setAttachments(value => omit(value, [fileId]))
    setFileSizeExceededCount(0)

    if (file.src) {
      URL.revokeObjectURL(file.src)
    }
  }

  const resetAttachments = () => {
    Object.values(attachments).forEach(file => {
      if (file.src) {
        URL.revokeObjectURL(file.src)
      }
    })

    setAttachments({})
    setFileSizeExceededCount(0)
  }

  useEffect(() => {
    return () => {
      // Clean up
      Object.values(attachments).forEach(file => {
        if (file.src) {
          URL.revokeObjectURL(file.src)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    attachments,
    fileSizeExceededCount,
    addAttachments,
    deleteAttachment,
    resetAttachments,
  }
}

export default useAttachments
